import Swiper from "https://cdn.jsdelivr.net/npm/swiper@12/swiper-bundle.min.mjs";

if (!customElements.get('products-grid')) {
    class ProductsGrid extends HTMLElement {
        // Class properties
        buttonShowClass = '.products-grid__show-all';
        filterButtonsClass = '.products-grid__filter--button';
        gridWrapperClass = '.products-grid__wrapper';
        slideClass = '.products-grid__slide';
        swiperClass = '.swiper';

        // Instance properties
        mediaQuerys = '(max-width: 1000px)';
        carouselModes = ['carousel'];
        defaultLimit = 12;
        elementsShown = [];
        availableFilters = new Set();
        activeFilter = null;

        constructor() {
            super();
            this.initVariables();
            this.checkFilters();
            this.eventListeners();
        }

        initVariables() {
            this.buttonShowElement = document.querySelector(this.buttonShowClass);
            this.filterButtons = this.querySelectorAll(this.filterButtonsClass);
            this.productsGrid = this.querySelector(this.gridWrapperClass);
            this.swiperElement = this.querySelector(this.swiperClass);
            this.allProducts = this.productsGrid.querySelectorAll(this.slideClass);

            this.mediaQuery = window.matchMedia(this.mediaQuerys);
            this.desktopViewMode = this.dataset.desktopView;
            this.mobileViewMode = this.dataset.mobileView;
            this.limit = parseInt(this.dataset.limit) || this.defaultLimit;
        }

        checkFilters() {
            if (this.filterButtons.length === 0) {
                return;
            }

            this.filterButtons.forEach((button) => {
                const filter = button.getAttribute('data-filter');
                const hasProducts = Array.from(this.allProducts).some((item) =>
                    item.dataset.filterTags
                        ?.split(',')
                        .includes(filter)
                );

                if (!hasProducts) {
                    button.remove();
                    return;
                }

                this.availableFilters.add(filter);
            });

            this.allProducts.forEach((item) => {
                if (!item.dataset.filterTags) {
                    item.remove();
                    return;
                }

                const filterTags = item.dataset.filterTags.split(',');
                let hasProducts = false;

                filterTags?.forEach((filter) => {
                    if (this.availableFilters.has(filter)) {
                        hasProducts = true;
                    }
                });
                if (!hasProducts) {
                    item.remove();
                }
            });
            if (this.availableFilters.size === 1) {
                this.filterButtons.forEach((button) => button.remove());
            }
        }

        eventListeners() {
            this.mediaQuery.addEventListener('change', this.handleViewportChange.bind(this));

            // Initialize based on current viewport
            if (this.carouselModes.includes(this.mobileViewMode) && this.mediaQuery.matches) {
                this.initializeCarousel();
            }

            this.buttonShowElement?.addEventListener('click', (e) => {
                e.preventDefault();
                this.buttonShowElement?.classList.add('hidden');
                this.allProducts.forEach((item) => {  
                    item.style.display = 'block';
                });
            });
        }

        initializeCarousel() {
            if (this.slider) {
                this.slider.destroy(true, true);
            }

            // Initialize Swiper directly
            this.slider = new Swiper(this.swiperElement, {
                slidesPerView: 2,
                spaceBetween: 4,
                scrollbar: {
                    el: '.swiper-scrollbar',
                    draggable: true,
                },
                navigation: {
                    nextEl: '.swiper-button-next',
                    prevEl: '.swiper-button-prev',
                }
            });
            this.filterButtons.forEach((button) => {
                if (button.getAttribute('aria-selected') === 'true') {
                    this.applyFilters(button);
                }
            });
        }

        handleViewportChange() {
            // Mobile view
            if (this.mediaQuery.matches && this.carouselModes.includes(this.mobileViewMode)) {
                this.initializeCarousel();
                return;
            }

            // Desktop view - destroy carousel if it exists
            if (this.slider) {
                this.slider.destroy(true, true);
                this.slider = null;
            }
        }

        applyFilters(filterButton) {
            const filter = filterButton.getAttribute('data-filter');

            this.filterButtons.forEach((button) => {
                button.setAttribute('aria-selected', String(filterButton === button));
            });
            this.activeFilter = filter;
            this.filterProducts();
        }

        filterProducts() {
            if (this.allProducts.length === 0) {
                return;
            }

            this.elementsShown = [];

            this.allProducts?.forEach((item) => {
                const hasFilterTag = item.dataset.filterTags
                    ?.split(',')
                    .includes(this.activeFilter);

                if (hasFilterTag) {
                    this.elementsShown.push(item);
                }

                item.style.display = 'none';
            });

            if (!this.slider) {
                this.showElements();
                this.buttonShowElement?.classList.toggle('hidden', this.elementsShown.length <= this.limit);
                this.buttonShowElement?.setAttribute('aria-disabled', String(this.elementsShown.length <= this.limit));
                return;
            }

            this.slider?.removeAllSlides();
            this.elementsShown.forEach((item) => {
                this.slider?.appendSlide(item);
            });
            this.showElements(this.elementsShown.length);
            this.slider?.update();
        }

        showElements(size = this.limit) {
            this.elementsShown
                .slice(0, size)
                .forEach((item) => {
                    item.style.display = 'block';
                });
        }

        handleFiltersClick() {
            this.filterButtons.forEach((button) => {
                button.addEventListener('click', (e) => {
                    e.preventDefault();
                    if (button.getAttribute('aria-selected') === 'true') {
                        return;
                    }
                    this.applyFilters(button);
                });
            });
        }

        connectedCallback() {
            this.handleFiltersClick();
            if (this.filterButtons.length > 0) {
                this.applyFilters(this.filterButtons[0]);
            }
            this.classList.remove('loading');
        }
    }

    customElements.define('products-grid', ProductsGrid);
}
